import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './QuizSettingsModal.css';
import { getWordsByUnit, getUnitIdFromStorage } from '../../services/wordService';

const QuizSettingsModal = ({ visible, onClose, quizType, showOrderOption = false }) => {
  const navigate = useNavigate();
  const [wordsData, setWordsData] = useState(null);
  const [unitInfo, setUnitInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // 题目来源：'random' 或 'custom'
  const [questionSource, setQuestionSource] = useState('random');
  
  // 题目数量
  const [questionCount, setQuestionCount] = useState(10);
  
  // 题目类型：单选
  const [questionType, setQuestionType] = useState('cn-en');
  
  // 随机题型开关
  const [isRandomType, setIsRandomType] = useState(false);
  
  // 题目顺序：'asc'（正序）或 'random'（乱序）
  const [order, setOrder] = useState('asc');
  
  // 自选的词汇
  const [customSelectedWords, setCustomSelectedWords] = useState([]);
  
  // 用于缓存的单元ID
  const [cachedUnitId, setCachedUnitId] = useState(null);
  
  // 规则说明弹窗
  const [showRules, setShowRules] = useState(false);

  // 获取弹窗标题
  const getModalTitle = () => {
    const titles = {
      '蓝': '题量赛设置',
      '黄': '计时赛设置',
      '红': '抢答赛设置'
    };
    return titles[quizType] || '比赛设置';
  };

  // 获取比赛规则说明
  const getRulesContent = () => {
    const rules = {
      '蓝': {
        title: '题量赛规则',
        content: [
          '1. 双方各自独立答题，互不干扰',
          '2. 每位同学需完成设定数量的题目',
          '3. 计分规则：答对得1分',
          '4. 胜负判定：',
          '   • 优先看得分：分数高者获胜',
          '   • 得分相同则看用时：用时少者获胜',
          '   • 得分及用时都相同则为平局'
        ]
      },
      '黄': {
        title: '计时赛规则',
        content: [
          '1. 计时赛总时长 = 所选单词数量 × 3秒，倒计时结束即比赛终止',
          '2. 初始题目完成后，系统将自动打乱题目顺序，继续出题直至计时结束',
          '3. 计分规则：答对得1分，答错扣1分（分数最低为0分，不设负分）',
          '4. 胜负判定：',
          '   • 优先看得分：分数高者获胜',
          '   • 得分相同看准度：正确率高者获胜',
          '   • 得分及准度都相同则为平局'
        ]
      },
      '红': {
        title: '抢答赛规则',
        content: [
          '1. 双方面对相同题目和选项进行抢答',
          '2. 抢答机制：第一个答对的人得分',
          '3. 计分规则：只有第一个答对者 +1分',
          '4. 换题条件：',
          '   • 任一方答对后立即进入下一题',
          '   • 双方都答完（无论对错）进入下一题',
          '5. 胜负判定：',
          '   • 优先看得分：分数高者获胜',
          '   • 得分相同看准度：正确率高者获胜',
          '   • 得分及准度都相同则为平局',
          '6. 注意：即使同时点击，也只有反应更快的人得分'
        ]
      }
    };
    return rules[quizType] || { title: '比赛规则', content: [] };
  };

  // 加载单元数据
  useEffect(() => {
    if (visible) {
      loadUnitData();
      // 检查是否从词汇选择页面返回
      checkReturnFromWordSelection();
    }
  }, [visible]);

  // 监听localStorage变化，当弹窗开启时实时更新数据
  useEffect(() => {
    if (!visible) return;

    const handleStorageChange = (e) => {
      // 监听 selectedTextbook 的变化
      if (e.key === 'selectedTextbook') {
        console.log('检测到单元数据变化，重新加载...');
        // 清除缓存，强制重新加载
        setCachedUnitId(null);
        setWordsData(null);
        loadUnitData();
        // 重置自选词汇
        setCustomSelectedWords([]);
        setQuestionSource('random');
        setQuestionType('cn-en');
        setIsRandomType(false);
      }
    };

    // 添加事件监听
    window.addEventListener('storage', handleStorageChange);

    // 由于同一个页面的localStorage变化不会触发storage事件
    // 我们需要自定义事件来处理同页面的更新
    const handleCustomStorageChange = (e) => {
      if (e.detail && e.detail.key === 'selectedTextbook') {
        console.log('检测到同页面单元数据变化，重新加载...');
        // 清除缓存，强制重新加载
        setCachedUnitId(null);
        setWordsData(null);
        loadUnitData();
        // 重置自选词汇
        setCustomSelectedWords([]);
        setQuestionSource('random');
        setQuestionType('cn-en');
        setIsRandomType(false);
      }
    };

    window.addEventListener('customStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('customStorageChange', handleCustomStorageChange);
    };
  }, [visible]);

  // 加载单元数据
  const loadUnitData = async () => {
    try {
      // 从localStorage获取单元信息
      const savedData = localStorage.getItem('selectedTextbook');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        setUnitInfo(parsed.unit);
      }

      // 获取单元ID
      const unitId = getUnitIdFromStorage();
      if (!unitId) {
        return;
      }
      
      // 检查是否已经缓存了相同单元的数据（组件内部缓存）
      if (cachedUnitId === unitId && wordsData) {
        return;
      }
      
      // 检查 sessionStorage 中是否有缓存数据（跨页面缓存）
      const cachedData = sessionStorage.getItem('quizUnitWordsData');
      if (cachedData) {
        try {
          const { unitId: cachedUnitId, data: cachedWordsData, timestamp } = JSON.parse(cachedData);
          // 检查缓存是否有效（5分钟内）
          const cacheAge = Date.now() - timestamp;
          const maxCacheAge = 5 * 60 * 1000; // 5分钟
          
          if (cachedUnitId === unitId && cacheAge < maxCacheAge) {
            setWordsData(cachedWordsData);
            setCachedUnitId(unitId);
            
            // 计算总词汇数并设置默认题目数量
            if (cachedWordsData && cachedWordsData.lessons) {
              const totalWords = cachedWordsData.lessons.reduce((total, lesson) => {
                return total + (lesson.words ? lesson.words.length : 0);
              }, 0);
              
              // 仅在题目来源为随机时设置题目数量
              if (questionSource === 'random') {
                if (totalWords >= 10) {
                  setQuestionCount(10);
                } else if (totalWords > 0) {
                  setQuestionCount(totalWords);
                }
              }
            }
            return;
          }
        } catch (err) {
          console.error('解析 sessionStorage 缓存数据失败:', err);
          // 继续正常加载流程
        }
      }
      
      setLoading(true);
      
      // 获取单元的所有词汇
      const data = await getWordsByUnit(unitId, { is_active: true, source_type: '3' });
      setWordsData(data);
      setCachedUnitId(unitId); // 缓存单元ID
      
      // 将数据存储到 sessionStorage 供跨页面使用
      sessionStorage.setItem('quizUnitWordsData', JSON.stringify({
        unitId,
        data,
        timestamp: Date.now()
      }));
      
      // 计算总词汇数并设置默认题目数量为最小值
      if (data && data.lessons) {
        const totalWords = data.lessons.reduce((total, lesson) => {
          return total + (lesson.words ? lesson.words.length : 0);
        }, 0);
        
        // 仅在题目来源为随机时设置题目数量
        if (questionSource === 'random') {
          if (totalWords >= 10) {
            setQuestionCount(10);
          } else if (totalWords > 0) {
            setQuestionCount(totalWords);
          }
        }
      }
    } catch (error) {
      console.error('加载单元数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 检查是否从词汇选择页面返回
  const checkReturnFromWordSelection = () => {
    const selectedWords = localStorage.getItem('quizCustomSelectedWords');
    if (selectedWords) {
      try {
        const words = JSON.parse(selectedWords);
        setCustomSelectedWords(words);
        setQuestionSource('custom');
        
        // 根据自选词汇数量设置题目数量为最小值
        const wordCount = words.length;
        if (wordCount >= 10) {
          setQuestionCount(10);
        } else if (wordCount > 0) {
          setQuestionCount(wordCount);
        }
        
        // 清除临时存储
        localStorage.removeItem('quizCustomSelectedWords');
      } catch (error) {
        console.error('解析自选词汇失败:', error);
      }
    }
  };

  // 计算总词汇数量（只计算可用于PK模式的单词，即is_translate为true或未设置的单词）
  const getTotalWordCount = () => {
    if (!wordsData || !wordsData.lessons) return 0;
    return wordsData.lessons.reduce((total, lesson) => {
      // 过滤掉is_translate为false的单词
      const availableWords = lesson.words ? lesson.words.filter(word => word.is_translate !== false) : [];
      return total + availableWords.length;
    }, 0);
  };

  // 获取可选的题目数量选项
  const getQuestionCountOptions = () => {
    const totalWords = getTotalWordCount();
    const options = [];
    
    // 检查总词汇数是否是10的倍数
    const isTenMultiple = totalWords % 10 === 0;
    
    // 基础选项：10, 15, 20
    if (totalWords >= 10) options.push(10);
    if (totalWords >= 15) options.push(15);
    if (totalWords >= 20) options.push(20);
    
    // 根据总词汇数量添加额外选项
    // 从30开始，每10个一档，直到总词汇数量
    if (totalWords > 20) {
      // 如果总词汇数是10的倍数，循环到 totalWords 之前（不包含 totalWords）
      // 如果不是10的倍数，循环到最接近的10的倍数
      const maxLoopValue = isTenMultiple ? totalWords : Math.floor(totalWords / 10) * 10;
      
      for (let i = 30; i <= maxLoopValue && i < totalWords; i += 10) {
        options.push(i);
      }
    }
    
    // 添加"全选"选项
    if (totalWords > 0) {
      options.push(totalWords);
    }
    
    return options;
  };

  // 获取所有可用于PK模式的单词（过滤掉is_translate为false的单词）
  const getAvailableWords = () => {
    if (!wordsData || !wordsData.lessons) return [];
    const allWords = [];
    wordsData.lessons.forEach(lesson => {
      if (lesson.words) {
        // 只包含is_translate不为false的单词
        const availableWords = lesson.words.filter(word => word.is_translate !== false);
        allWords.push(...availableWords);
      }
    });
    return allWords;
  };

  // 处理题目来源改变
  const handleSourceChange = (source) => {
    setQuestionSource(source);
    if (source === 'custom') {
      // 跳转到词汇选择页面
      // 保存当前状态到localStorage
      localStorage.setItem('quizSettingsState', JSON.stringify({
        quizType,
        questionType,
        questionCount
      }));
      // 设置标记，告诉Word页面进入选择模式（PK模式）
      localStorage.setItem('wordSelectionMode', 'quiz');
      navigate('/word');
    }
  };

  // 处理开始比赛
  const handleStartQuiz = () => {
    if (questionSource === 'random' && questionCount === 0) {
      alert('请选择题目数量');
      return;
    }
    
    if (questionSource === 'custom' && customSelectedWords.length === 0) {
      alert('请先选择词汇');
      return;
    }

    // 获取所有可用的单词（过滤掉is_translate为false的单词）
    const availableWords = getAvailableWords();
    
    // 验证是否有足够的可用单词
    if (availableWords.length === 0) {
      alert('当前单元没有可用的单词');
      return;
    }

    // 准备比赛配置
    const quizConfig = {
      quizType,
      questionSource,
      questionType: isRandomType ? 'random' : questionType, // 随机开关打开时使用'random'
      order, // 新增题目顺序
      questionCount: questionSource === 'random' ? questionCount : customSelectedWords.length,
      customWords: questionSource === 'custom' ? customSelectedWords : null,
      availableWords, // 添加可用单词列表供比赛页面使用
      unitInfo
    };

    // 保存配置到localStorage
    localStorage.setItem('quizConfig', JSON.stringify(quizConfig));

    // 进入全屏模式
    const enterFullscreen = async () => {
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { // Safari
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { // IE11
          await elem.msRequestFullscreen();
        }
      } catch (err) {
        console.warn('⚠️ 进入全屏失败:', err);
      }
    };
    
    // 先进入全屏，再跳转页面
    enterFullscreen().finally(() => {
      // 根据比赛类型跳转到不同页面
      let targetRoute = '/quantity-game'; // 默认题量赛
      if (quizType === '黄') {
        targetRoute = '/timed-game'; // 计时赛
      } else if (quizType === '红') {
        targetRoute = '/rush-answer-game'; // 抢答赛
      }
      navigate(targetRoute);
      onClose();
    });
  };

  if (!visible) return null;

  return (
    <div className="quiz-settings-modal-overlay" onClick={onClose}>
      <div className="quiz-settings-modal" onClick={(e) => e.stopPropagation()}>
        {/* 标题 */}
        <div className="quiz-settings-header">
          <div className="quiz-settings-title-wrapper">
            <h2>{getModalTitle()}</h2>
            <button 
              className="quiz-rules-icon" 
              onClick={(e) => {
                e.stopPropagation();
                setShowRules(true);
              }}
              title="查看规则说明"
            >
              !
            </button>
          </div>
        </div>

        {/* 内容 */}
        <div className="quiz-settings-content">
          {loading ? (
            <div className="quiz-settings-loading">加载中...</div>
          ) : (
            <>
              {/* 所选单元信息 */}
              <div className="quiz-settings-section">
                <h3>所选单元</h3>
                <div className="quiz-settings-unit-info">
                  <div className="unit-info-item">
                    <span className="unit-info-label">单元标题：</span>
                    <span className="unit-info-value">
                      {unitInfo ? unitInfo.title : '未选择单元'}
                    </span>
                  </div>
                  <div className="unit-info-item">
                    <span className="unit-info-label">词汇数量：</span>
                    <span className="unit-info-value">{getTotalWordCount()} 个</span>
                  </div>
                </div>
              </div>

              {/* 题目来源 */}
              <div className="quiz-settings-section">
                <h3>题目来源</h3>
                <div className="quiz-settings-options">
                  <label className="quiz-option-item">
                    <input
                      type="radio"
                      name="questionSource"
                      value="random"
                      checked={questionSource === 'random'}
                      onChange={(e) => setQuestionSource(e.target.value)}
                    />
                    <span>随机</span>
                  </label>
                  <label className="quiz-option-item">
                    <input
                      type="radio"
                      name="questionSource"
                      value="custom"
                      checked={questionSource === 'custom'}
                      onChange={(e) => handleSourceChange(e.target.value)}
                    />
                    <span>自选</span>
                  </label>
                </div>
              </div>

              {/* 题目数量 - 仅在随机模式下显示 */}
              {questionSource === 'random' && (
                <div className="quiz-settings-section">
                  <h3>题目数量</h3>
                  <div className="quiz-settings-count-options">
                    {getQuestionCountOptions().map((count, index, array) => {
                      const isLastOption = index === array.length - 1;
                      const isAllOption = count === getTotalWordCount();
                      return (
                        <button
                          key={count}
                          className={`count-option-btn ${questionCount === count ? 'active' : ''}`}
                          onClick={() => setQuestionCount(count)}
                        >
                          {isAllOption && isLastOption ? '全选' : `${count}题`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 自选词汇数量显示 */}
              {questionSource === 'custom' && (
                <div className="quiz-settings-section">
                  <h3>已选词汇</h3>
                  <div className="custom-words-count">
                    <span className="custom-count-value">{customSelectedWords.length}</span>
                    <span className="custom-count-label"> 个词汇</span>
                    {customSelectedWords.length > 0 && (
                      <button 
                        className="reselect-btn"
                        onClick={() => handleSourceChange('custom')}
                      >
                        重新选择
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* 题目类型 */}
              <div className="quiz-settings-section">
                <div className="quiz-settings-header-row">
                  <h3>题目类型</h3>
                  <div className="random-switch-container">
                    <span className="random-switch-label">随机题型</span>
                    <label className="ios-switch">
                      <input
                        type="checkbox"
                        checked={isRandomType}
                        onChange={(e) => setIsRandomType(e.target.checked)}
                      />
                      <span className="ios-slider"></span>
                    </label>
                  </div>
                </div>
                <div className="quiz-settings-options quiz-type-options">
                  <label className="quiz-option-item">
                    <input
                      type="checkbox"
                      checked={isRandomType || questionType === 'cn-en'}
                      onChange={(e) => {
                        if (!isRandomType) {
                          setQuestionType('cn-en');
                        }
                      }}
                      disabled={isRandomType}
                    />
                    <span>中译英</span>
                  </label>
                  <label className="quiz-option-item">
                    <input
                      type="checkbox"
                      checked={isRandomType || questionType === 'en-cn'}
                      onChange={(e) => {
                        if (!isRandomType) {
                          setQuestionType('en-cn');
                        }
                      }}
                      disabled={isRandomType}
                    />
                    <span>英译中</span>
                  </label>
                  {/* 听音选图 - 只在抢答赛时显示 */}
                  {quizType === '红' && (
                    <label className="quiz-option-item">
                      <input
                        type="checkbox"
                        checked={isRandomType || questionType === 'listen-picture'}
                        onChange={(e) => {
                          if (!isRandomType) {
                            setQuestionType('listen-picture');
                          }
                        }}
                        disabled={isRandomType}
                      />
                      <span>听音选图</span>
                    </label>
                  )}
                </div>
              </div>
              
              {/* 题目顺序 - 仅在showOrderOption为true时显示 */}
              {showOrderOption && (
                <div className="quiz-settings-section">
                  <h3>题目顺序</h3>
                  <div className="quiz-settings-options">
                    <label className="quiz-option-item">
                      <input
                        type="radio"
                        name="questionOrder"
                        value="asc"
                        checked={order === 'asc'}
                        onChange={(e) => setOrder(e.target.value)}
                      />
                      <span>正序</span>
                    </label>
                    <label className="quiz-option-item">
                      <input
                        type="radio"
                        name="questionOrder"
                        value="random"
                        checked={order === 'random'}
                        onChange={(e) => setOrder(e.target.value)}
                      />
                      <span>乱序</span>
                    </label>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="quiz-settings-footer">
          <button 
            className="quiz-start-btn" 
            onClick={handleStartQuiz}
            disabled={loading || (questionSource === 'custom' && customSelectedWords.length === 0)}
          >
            开始比赛
          </button>
          <button className="quiz-cancel-btn" onClick={onClose}>
            取消
          </button>
        </div>
      </div>

      {/* 规则说明弹窗 */}
      {showRules && (
        <div className="quiz-rules-overlay" onClick={() => setShowRules(false)}>
          <div className="quiz-rules-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quiz-rules-header">
              <h3>{getRulesContent().title}</h3>
              <button className="quiz-rules-close" onClick={() => setShowRules(false)}>×</button>
            </div>
            <div className="quiz-rules-content">
              {getRulesContent().content.map((rule, index) => (
                <p key={index} className="quiz-rule-item">{rule}</p>
              ))}
            </div>
            <div className="quiz-rules-footer">
              <button className="quiz-rules-ok-btn" onClick={() => setShowRules(false)}>
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizSettingsModal;

